// client/src/hooks/useAnalyticsData.js
import { useState, useCallback, useMemo } from 'react'
import ApiAnalytics from '../api/ApiAnalytics'
import ApiAdminTools from '../api/ApiAdminTools'
import ApiPlayers from '../api/ApiPlayers'
import toastr from 'toastr'

const FLOW_DATA_LIMIT = 100 // Maximum number of page transitions to fetch for flow visualization

/**
 * Custom hook to manage all analytics data fetching and loading states
 *
 * Manages:
 * - 7 different data endpoints (metrics, events, health, DAU, events by user, flow, players)
 * - 7 separate loading states
 * - Error handling for all API calls
 *
 * @returns {Object} Data state and loading methods
 */
export default function useAnalyticsData() {
  // Data state
  const [metrics, setMetrics] = useState(null)
  const [events, setEvents] = useState([])
  const [healthData, setHealthData] = useState(null)
  const [performanceData, setPerformanceData] = useState(null)
  const [dailyActiveUsersDetailed, setDailyActiveUsersDetailed] = useState([])
  const [eventsByUser, setEventsByUser] = useState([])
  const [flowData, setFlowData] = useState(null)
  const [players, setPlayers] = useState([])

  // Loading states
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [eventsLoading, setEventsLoading] = useState(true)
  const [healthLoading, setHealthLoading] = useState(true)
  const [dailyActiveUsersLoading, setDailyActiveUsersLoading] = useState(true)
  const [eventsByUserLoading, setEventsByUserLoading] = useState(true)
  const [flowLoading, setFlowLoading] = useState(true)
  const [playersLoading, setPlayersLoading] = useState(false)

  // Error states
  const [error, setError] = useState(null)
  const [flowError, setFlowError] = useState(null)

  /**
   * Calculate date range for API calls
   * @param {number} days - Number of days to look back
   * @returns {Object} { startDate, endDate }
   */
  const getDateRange = (days) => {
    const endDateTime = new Date()
    endDateTime.setHours(23, 59, 59, 999) // End of today
    const endDate = endDateTime.toISOString().split('T')[0]

    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)
    const startDateStr = startDate.toISOString().split('T')[0]

    return { startDate: startDateStr, endDate }
  }

  /**
   * Load analytics metrics
   * @param {number} days - Number of days to fetch (default: 30)
   */
  const loadMetrics = useCallback(async (days = 30) => {
    try {
      setMetricsLoading(true)
      setError(null)

      const { startDate, endDate } = getDateRange(days)

      const metricsData = await ApiAnalytics.getMetrics(
        startDate,
        endDate,
        15, // Top 15 features
      )
      setMetrics(metricsData.metrics)
    } catch (err) {
      console.error('Failed to load analytics metrics:', err)
      setError(err.message || 'Failed to load analytics data')
      toastr.error('Failed to load analytics data')
    } finally {
      setMetricsLoading(false)
    }
  }, [])

  /**
   * Load telemetry events
   * @param {number|null} playerId - Optional player ID to filter by
   * @param {Object} filters - Filter options { excludeTag, excludeAdminUsers }
   * @param {number} offset - Pagination offset (default: 0)
   */
  const loadEvents = useCallback(
    async (playerId = null, filters = {}, offset = 0) => {
      try {
        setEventsLoading(true)

        const { excludeTag = null, excludeAdminUsers = false } = filters

        const eventsData = await ApiAnalytics.getEvents(
          100,
          null,
          playerId,
          excludeTag,
          offset,
          excludeAdminUsers,
        )

        setEvents(eventsData.events || [])
      } catch (err) {
        console.error('Failed to load events:', err)
        toastr.error('Failed to load events')
      } finally {
        setEventsLoading(false)
      }
    },
    [],
  )

  /**
   * Load more events for pagination (appends to existing events)
   * @param {number|null} playerId - Optional player ID to filter by
   * @param {Object} filters - Filter options { excludeTag, excludeAdminUsers }
   * @param {number} offset - Pagination offset
   * @returns {Object} { newEvents, hasMore }
   */
  const loadMoreEvents = useCallback(
    async (playerId = null, filters = {}, offset = 0) => {
      try {
        const { excludeTag = null, excludeAdminUsers = false } = filters

        const eventsData = await ApiAnalytics.getEvents(
          100,
          null,
          playerId,
          excludeTag,
          offset,
          excludeAdminUsers,
        )

        const newEvents = eventsData.events || []
        setEvents((prevEvents) => [...prevEvents, ...newEvents])

        return {
          newEvents,
          hasMore: newEvents.length === 100,
        }
      } catch (err) {
        console.error('Failed to load more events:', err)
        toastr.error('Failed to load more events')
        return {
          newEvents: [],
          hasMore: false,
        }
      }
    },
    [],
  )

  /**
   * Load detailed daily active users data
   * @param {number} days - Number of days to fetch (default: 30)
   */
  const loadDailyActiveUsersDetailed = useCallback(async (days = 30) => {
    try {
      setDailyActiveUsersLoading(true)

      const { startDate, endDate } = getDateRange(days)

      const detailedData = await ApiAnalytics.getDailyActiveUsersDetailed(
        startDate,
        endDate,
      )
      setDailyActiveUsersDetailed(
        detailedData.daily_active_users_detailed || [],
      )
    } catch (err) {
      console.error('Failed to load daily active users detailed:', err)
      toastr.error('Failed to load detailed user data')
    } finally {
      setDailyActiveUsersLoading(false)
    }
  }, [])

  /**
   * Load events by user
   * @param {number} days - Number of days to fetch (default: 30)
   */
  const loadEventsByUser = useCallback(async (days = 30) => {
    try {
      setEventsByUserLoading(true)

      const { startDate, endDate } = getDateRange(days)

      const eventsByUserData = await ApiAnalytics.getEventsByUser(
        startDate,
        endDate,
        20, // Top 20 users
      )
      setEventsByUser(eventsByUserData.events_by_user || [])
    } catch (err) {
      console.error('Failed to load events by user:', err)
      toastr.error('Failed to load events by user data')
    } finally {
      setEventsByUserLoading(false)
    }
  }, [])

  /**
   * Load user journey flow data
   * @param {number} days - Number of days to fetch (default: 30)
   */
  const loadFlowData = useCallback(async (days = 30, options = {}) => {
    try {
      setFlowLoading(true)
      setFlowError(null)

      const { startDate, endDate } = getDateRange(days)

      const flowResponse = await ApiAdminTools.getPageFlows(
        startDate,
        endDate,
        FLOW_DATA_LIMIT,
        options,
      )

      if (flowResponse.success) {
        setFlowData(flowResponse)
      } else {
        setFlowError(flowResponse.message || 'Failed to load flow data')
        toastr.error('Failed to load user journey flow data')
      }
    } catch (err) {
      console.error('Failed to load flow data:', err)
      setFlowError(err.message || 'Failed to load flow data')
      toastr.error('Failed to load user journey flow data')
    } finally {
      setFlowLoading(false)
    }
  }, [])

  /**
   * Load system health data
   */
  const loadHealthData = useCallback(async () => {
    try {
      setHealthLoading(true)
      const [healthResponse, performanceResponse] = await Promise.all([
        ApiAnalytics.getHealth(),
        ApiAnalytics.getPerformance(),
      ])

      setHealthData(healthResponse)
      setPerformanceData(performanceResponse)
    } catch (err) {
      console.error('Failed to load health data:', err)
      toastr.error('Failed to load system health data')
    } finally {
      setHealthLoading(false)
    }
  }, [])

  /**
   * Load all players data
   */
  const loadPlayers = useCallback(async () => {
    try {
      setPlayersLoading(true)
      const playersData = await ApiPlayers.getAll()
      setPlayers(playersData || [])
    } catch (err) {
      console.error('Failed to load players:', err)
      toastr.error('Failed to load players data')
    } finally {
      setPlayersLoading(false)
    }
  }, [])

  /**
   * Refresh all data based on current filters
   * @param {number} days - Number of days for date-based queries
   */
  const refresh = useCallback(
    async (days = 30) => {
      await Promise.all([
        loadMetrics(days),
        loadDailyActiveUsersDetailed(days),
        loadEventsByUser(days),
        loadFlowData(days),
        loadHealthData(),
      ])
    },
    [
      loadMetrics,
      loadDailyActiveUsersDetailed,
      loadEventsByUser,
      loadFlowData,
      loadHealthData,
    ],
  )

  return useMemo(
    () => ({
      // Data
      metrics,
      events,
      healthData,
      performanceData,
      dailyActiveUsersDetailed,
      eventsByUser,
      flowData,
      players,

      // Loading states
      metricsLoading,
      eventsLoading,
      healthLoading,
      dailyActiveUsersLoading,
      eventsByUserLoading,
      flowLoading,
      playersLoading,

      // Errors
      error,
      flowError,

      // Methods
      loadMetrics,
      loadEvents,
      loadMoreEvents,
      loadDailyActiveUsersDetailed,
      loadEventsByUser,
      loadFlowData,
      loadHealthData,
      loadPlayers,
      refresh,
    }),
    // Only include state values, not callbacks (already stable via useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      metrics,
      events,
      healthData,
      performanceData,
      dailyActiveUsersDetailed,
      eventsByUser,
      flowData,
      players,
      metricsLoading,
      eventsLoading,
      healthLoading,
      dailyActiveUsersLoading,
      eventsByUserLoading,
      flowLoading,
      playersLoading,
      error,
      flowError,
    ],
  )
}
